import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import {
  invoices as mockInvoices,
  invoiceStatusLabels,
  invoiceStatusColors,
  type Invoice,
  type InvoiceItem,
} from '@/mocks/superAdminInvoices';
import InvoiceDetailModal from './components/InvoiceDetailModal';
import InvoiceFormModal, { type InvoiceFormData } from './components/InvoiceFormModal';

type LoadingState = 'loading' | 'ready' | 'error';
type InvoiceStatus = Invoice['status'];

const ALL_STATUSES: (InvoiceStatus | 'todos')[] = ['todos', 'draft', 'pending', 'paid', 'overdue', 'cancelled', 'refunded'];

function formatCurrency(n: number): string {
  return `$${n.toFixed(2)}`;
}

function simulateLoad(): Promise<Invoice[]> {
  return new Promise((resolve, reject) => {
    const delay = 700 + Math.random() * 400;
    setTimeout(() => {
      if (Math.random() > 0.94) reject(new Error('Timeout al conectar con el servidor.'));
      else resolve(mockInvoices);
    }, delay);
  });
}

function showSweetAlert(icon: 'success' | 'error' | 'warning' | 'info', title: string, text?: string) {
  return Swal.fire({
    icon, title, text: text ?? '',
    confirmButtonColor: '#1a1a1a',
    customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
  });
}

export default function SuperAdminInvoices() {
  const navigate = useNavigate();
  const [data, setData] = useState<Invoice[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [activeStatus, setActiveStatus] = useState<string>('todos');
  const [searchTerm, setSearchTerm] = useState('');

  const [viewInvoice, setViewInvoice] = useState<Invoice | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const loadData = useCallback(() => {
    setLoadingState('loading'); setErrorMsg(null);
    simulateLoad().then((d) => { setData(d); setLoadingState('ready'); }).catch((err) => { setErrorMsg(err.message || 'Error desconocido'); setLoadingState('error'); });
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const statusCounts = useMemo(() => {
    const c: Record<string, number> = { todos: data.length };
    ALL_STATUSES.filter((s) => s !== 'todos').forEach((s) => { c[s] = data.filter((inv) => inv.status === s).length; });
    return c;
  }, [data]);

  const filtered = useMemo(() => {
    let r = data;
    if (activeStatus !== 'todos') r = r.filter((inv) => inv.status === activeStatus);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      r = r.filter((inv) => inv.number.toLowerCase().includes(q) || inv.radioName.toLowerCase().includes(q) || inv.frequency.toLowerCase().includes(q));
    }
    return r;
  }, [data, activeStatus, searchTerm]);

  const openDetail = (inv: Invoice) => {
    setViewInvoice(inv);
    setDetailOpen(true);
  };

  const handleFormSave = (formData: InvoiceFormData) => {
    const { radioName, frequency, planName, concepto, items, discount, taxRate, dueDate, notes } = formData;
    const taxRateNum = parseFloat(taxRate) / 100;
    const subtotal = items.reduce((s, it) => s + it.total, 0);
    const afterDiscount = Math.max(0, subtotal - discount);
    const tax = Math.round(afterDiscount * taxRateNum * 100) / 100;
    const total = afterDiscount + tax;

    const now = new Date().toISOString().slice(0, 10);
    const num = `INV-2026-${String(data.length + 1).padStart(4, '0')}`;
    const newInvoice: Invoice = {
      id: Math.max(...data.map((i) => i.id), 0) + 1,
      number: num, radioName, frequency, planName, concepto,
      items, subtotal, discount, taxRate: taxRateNum, tax, total,
      status: 'draft', issuedAt: now, dueDate: dueDate || new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
      paidAt: null, notes: notes || undefined,
    };

    setData((prev) => [newInvoice, ...prev]);
    setFormOpen(false);
    showSweetAlert('success', 'Factura creada', `Se generó ${num} (borrador) por ${formatCurrency(total)}.`);
  };

  const handleMarkPaid = (inv: Invoice) => {
    Swal.fire({
      icon: 'question', title: '¿Marcar como pagada?',
      text: `La factura ${inv.number} de ${inv.radioName} se marcará como pagada.`,
      showCancelButton: true, confirmButtonText: 'Marcar pagada', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#059669', cancelButtonColor: '#e5e5e5',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
      buttonsStyling: false,
    }).then((r) => {
      if (r.isConfirmed) {
        setData((prev) => prev.map((x) => x.id === inv.id ? { ...x, status: 'paid' as InvoiceStatus, paidAt: new Date().toISOString().slice(0, 10) } : x));
        showSweetAlert('success', 'Factura pagada', `${inv.number} fue marcada como pagada.`);
      }
    });
  };

  const handleMarkOverdue = (inv: Invoice) => {
    Swal.fire({
      icon: 'warning', title: '¿Marcar como vencida?',
      text: `La factura ${inv.number} pasará a estado Vencida.`,
      showCancelButton: true, confirmButtonText: 'Marcar vencida', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626', cancelButtonColor: '#e5e5e5',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
      buttonsStyling: false,
    }).then((r) => {
      if (r.isConfirmed) {
        setData((prev) => prev.map((x) => x.id === inv.id ? { ...x, status: 'overdue' as InvoiceStatus } : x));
        showSweetAlert('success', 'Factura vencida', `${inv.number} fue marcada como vencida.`);
      }
    });
  };

  const handleCancel = (inv: Invoice) => {
    Swal.fire({
      icon: 'warning', title: '¿Cancelar factura?',
      text: `La factura ${inv.number} será cancelada. Esta acción es irreversible.`,
      showCancelButton: true, confirmButtonText: 'Cancelar factura', cancelButtonText: 'Volver',
      confirmButtonColor: '#6b7280', cancelButtonColor: '#e5e5e5',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
      buttonsStyling: false,
    }).then((r) => {
      if (r.isConfirmed) {
        setData((prev) => prev.map((x) => x.id === inv.id ? { ...x, status: 'cancelled' as InvoiceStatus } : x));
        showSweetAlert('success', 'Factura cancelada', `${inv.number} fue cancelada.`);
      }
    });
  };

  const handleDuplicate = (inv: Invoice) => {
    const num = `INV-2026-${String(data.length + 1).padStart(4, '0')}`;
    const copy: Invoice = {
      ...inv,
      id: Math.max(...data.map((i) => i.id), 0) + 1,
      number: num,
      items: inv.items.map((it) => ({ ...it, id: `it-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` })),
      status: 'draft' as InvoiceStatus,
      issuedAt: new Date().toISOString().slice(0, 10),
      dueDate: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
      paidAt: null,
      paymentId: undefined,
    };
    setData((prev) => [copy, ...prev]);
    showSweetAlert('success', 'Factura duplicada', `Se creó ${num} como copia de ${inv.number} (borrador).`);
  };

  const handleExport = (format: string) => {
    showSweetAlert('info', `Exportando ${format.toUpperCase()}`, `Archivo ${format} generado exitosamente (mock).`);
    setExportModalOpen(false);
  };

  const handlePrintInvoice = (inv: Invoice) => {
    const statusLabel = invoiceStatusLabels[inv.status];
    const itemsRows = inv.items.map((it: InvoiceItem) =>
      `<tr><td>${it.description}</td><td style="text-align:center">${it.quantity}</td><td style="text-align:right">$${it.unitPrice.toFixed(2)}</td><td style="text-align:right">$${it.total.toFixed(2)}</td></tr>`
    ).join('');
    const discountRow = inv.discount > 0
      ? `<tr><td colspan="3">Descuento</td><td style="text-align:right;color:#059669">-$${inv.discount.toFixed(2)}</td></tr>`
      : '';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${inv.number}</title><style>body{font-family:'Segoe UI',system-ui,sans-serif;margin:0;padding:40px;color:#1a1a1a}*{box-sizing:border-box}.header{display:flex;align-items:center;justify-content:space-between;margin-bottom:30px;padding-bottom:20px;border-bottom:2px solid #1a1a1a}.header-left h1{font-size:22px;font-weight:800;margin:0}.header-left p{margin:4px 0 0;color:#666;font-size:11px}.header-right{text-align:right}.header-right h2{margin:0;font-size:18px;font-weight:700}.header-right .inv-num{margin-top:6px;font-size:14px;font-weight:700}.info{display:flex;justify-content:space-between;margin-bottom:24px;gap:16px;flex-wrap:wrap}.info-block{flex:1;min-width:140px}.info-block h4{font-size:10px;color:#999;text-transform:uppercase;margin:0 0 4px;letter-spacing:.5px}.info-block p{font-size:12px;margin:2px 0;color:#444}table{width:100%;border-collapse:collapse;margin-bottom:24px}th{text-align:left;font-size:10px;color:#999;text-transform:uppercase;padding:8px 10px;border-bottom:2px solid #e5e5e5;letter-spacing:.5px}td{font-size:12px;padding:8px 10px;border-bottom:1px solid #f0f0f0}.totals{margin-left:auto;width:280px}.totals td{padding:6px 10px;font-size:12px}.totals .grand td{font-weight:700;font-size:14px;border-top:2px solid #1a1a1a;padding-top:8px}.footer{text-align:center;font-size:10px;color:#999;margin-top:30px;padding-top:16px;border-top:1px solid #e5e5e5}@media print{body{padding:20px}}</style></head><body><div class="header"><div class="header-left"><h1>RadioFrecuenciaWeb</h1><p>Plataforma de administración</p></div><div class="header-right"><h2>Factura</h2><p class="inv-num">${inv.number}</p></div></div><div class="info"><div class="info-block"><h4>Emisora</h4><p><strong>${inv.radioName}</strong></p><p>${inv.frequency}</p></div><div class="info-block"><h4>Concepto</h4><p>${inv.concepto || inv.planName}</p></div><div class="info-block"><h4>Estado</h4><span>${statusLabel}</span></div></div><div class="info"><div class="info-block"><h4>Emisión</h4><p>${inv.issuedAt}</p></div><div class="info-block"><h4>Vencimiento</h4><p>${inv.dueDate}</p></div><div class="info-block"><h4>Pago</h4><p>${inv.paidAt || '—'}</p></div></div><table><thead><tr><th>Concepto</th><th style="text-align:center">Cant.</th><th style="text-align:right">P. Unit.</th><th style="text-align:right">Total</th></tr></thead><tbody>${itemsRows}</tbody></table><table class="totals"><tr><td>Subtotal</td><td style="text-align:right">$${inv.subtotal.toFixed(2)}</td></tr>${discountRow}<tr><td>IVA (${(inv.taxRate * 100).toFixed(0)}%)</td><td style="text-align:right">$${inv.tax.toFixed(2)}</td></tr><tr class="grand"><td>TOTAL</td><td style="text-align:right">$${inv.total.toFixed(2)}</td></tr></table>${inv.notes ? `<div class="info"><div class="info-block"><h4>Notas</h4><p>${inv.notes}</p></div></div>` : ''}<div class="footer"><p>RadioFrecuenciaWeb — Factura generada el ${new Date().toLocaleDateString('es-AR')}</p></div></body></html>`;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
    iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = 'none';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) { doc.open(); doc.write(html); doc.close(); iframe.contentWindow?.focus(); iframe.contentWindow?.print(); setTimeout(() => { if (document.body.contains(iframe)) document.body.removeChild(iframe); }, 1500); }
  };

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <Link to="/sa/billing" className="hover:text-foreground-600 transition-colors cursor-pointer">Billing</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Facturas</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Facturas</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  {loadingState === 'ready' ? `${data.length} facturas` : 'Cargando...'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative w-full sm:w-52">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                  <input
                    type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar factura, radio..."
                    disabled={loadingState !== 'ready'}
                    className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50"
                  />
                  {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"><i className="ri-close-line text-foreground-500 text-[10px]"></i></button>}
                </div>
                <button onClick={() => setFormOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line text-sm"></i>Nueva Factura
                </button>
                <button onClick={() => setExportModalOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-background-200 bg-background-50 text-foreground-600 text-xs font-semibold hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-download-line text-sm"></i>Exportar
                </button>
              </div>
            </div>

            {/* Loading */}
            {loadingState === 'loading' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="divide-y divide-background-100">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4 px-4 py-3 animate-pulse">
                      <div className="w-24 h-3 bg-background-200 rounded"></div>
                      <div className="w-32 h-3 bg-background-200 rounded"></div>
                      <div className="w-16 h-3 bg-background-200 rounded ml-auto"></div>
                      <div className="w-16 h-5 bg-background-200 rounded-full"></div>
                      <div className="w-20 h-3 bg-background-200 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center"><i className="ri-error-warning-line text-red-400 text-2xl"></i></div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar datos</p><p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button onClick={loadData} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"><i className="ri-refresh-line text-sm"></i>Reintentar</button>
              </div>
            )}

            {/* Status chips */}
            {loadingState === 'ready' && (
              <div className="flex flex-wrap items-center gap-1">
                {ALL_STATUSES.map((s) => (
                  <button key={s} onClick={() => setActiveStatus(s)} className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap ${activeStatus === s ? 'bg-foreground-950 text-background-50' : 'bg-background-50 text-foreground-500 hover:bg-background-200 border border-background-200'}`}>
                    {s === 'todos' ? 'Todas' : invoiceStatusLabels[s as InvoiceStatus]} ({statusCounts[s] ?? 0})
                  </button>
                ))}
              </div>
            )}

            {/* Table */}
            {loadingState === 'ready' && (
              filtered.length === 0 ? (
                <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-inbox-line text-foreground-300 text-2xl"></i></div>
                  <p className="text-foreground-800 text-sm font-semibold mb-1">Sin resultados</p>
                  <button onClick={() => { setActiveStatus('todos'); setSearchTerm(''); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-200 text-foreground-600 text-xs font-bold hover:bg-background-300 transition-colors cursor-pointer"><i className="ri-filter-off-line text-sm"></i>Limpiar filtros</button>
                </div>
              ) : (
                <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                  {/* Desktop */}
                  <div className="hidden lg:block">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-background-200 bg-background-100/50">
                          {['Invoice', 'Radio', 'Total', 'Estado', 'Vence', 'Acciones'].map((h) => (
                            <th key={h} className="px-4 py-2.5 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-100">
                        {filtered.map((inv) => (
                          <tr key={inv.id} onClick={() => openDetail(inv)} className="hover:bg-background-100/50 transition-colors cursor-pointer">
                            <td className="px-4 py-3">
                              <span className="text-foreground-800 text-xs font-semibold font-mono">{inv.number}</span>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-foreground-800 text-xs font-semibold">{inv.radioName}</p>
                              <p className="text-foreground-400 text-[10px]">{inv.frequency}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-foreground-800 text-xs font-bold">${inv.total.toFixed(2)}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${invoiceStatusColors[inv.status]}`}>
                                {invoiceStatusLabels[inv.status]}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs ${inv.status === 'overdue' ? 'text-red-500 font-semibold' : 'text-foreground-600'}`}>
                                {inv.dueDate}
                              </span>
                            </td>
                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => openDetail(inv)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Ver detalle">
                                <i className="ri-eye-line text-foreground-500 text-xs"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile */}
                  <div className="lg:hidden divide-y divide-background-100">
                    {filtered.map((inv) => (
                      <div key={inv.id} onClick={() => openDetail(inv)} className="p-4 cursor-pointer hover:bg-background-100/50 transition-colors">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-foreground-800 text-xs font-semibold font-mono">{inv.number}</span>
                          <span className="text-foreground-800 text-xs font-bold">${inv.total.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-foreground-800 text-xs font-semibold">{inv.radioName}</p>
                            <p className="text-foreground-400 text-[10px]">{inv.frequency}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${invoiceStatusColors[inv.status]}`}>
                              {invoiceStatusLabels[inv.status]}
                            </span>
                            <span className={`text-[10px] ${inv.status === 'overdue' ? 'text-red-500 font-semibold' : 'text-foreground-400'}`}>
                              {inv.dueDate}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 flex justify-end">
                          <button onClick={(e) => { e.stopPropagation(); openDetail(inv); }} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Ver detalle">
                            <i className="ri-eye-line text-foreground-500 text-xs"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        </main>
      </div>

      {/* Detail Modal */}
      {viewInvoice && (
        <InvoiceDetailModal
          open={detailOpen}
          invoice={viewInvoice}
          onClose={() => { setDetailOpen(false); setViewInvoice(null); }}
          onMarkPaid={handleMarkPaid}
          onMarkOverdue={handleMarkOverdue}
          onCancel={handleCancel}
          onDuplicate={handleDuplicate}
          onPrint={handlePrintInvoice}
          onNavigate={(path) => navigate(path)}
        />
      )}

      {/* Create Wizard */}
      <InvoiceFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleFormSave}
      />

      {/* Export Modal */}
      {exportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setExportModalOpen(false)}></div>
          <div className="relative bg-background-50 rounded-2xl shadow-xl w-full max-w-xs">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200"><h3 className="text-foreground-950 font-bold text-sm">Exportar Facturas</h3><button onClick={() => setExportModalOpen(false)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200"><i className="ri-close-line text-foreground-500 text-sm"></i></button></div>
            <div className="p-5 space-y-2">
              {[
                { format: 'csv', icon: 'ri-file-excel-2-line', color: 'bg-emerald-50 text-emerald-600', label: 'CSV' },
                { format: 'excel', icon: 'ri-file-excel-2-line', color: 'bg-emerald-50 text-emerald-600', label: 'Excel (.xlsx)' },
                { format: 'pdf', icon: 'ri-file-pdf-2-line', color: 'bg-red-50 text-red-500', label: 'PDF' },
              ].map(exp => (
                <button key={exp.format} onClick={() => handleExport(exp.format)} className={`w-full flex items-center gap-3 p-3 rounded-xl ${exp.color.split(' ')[0]} hover:opacity-80 transition-opacity cursor-pointer text-left`}>
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center"><i className={`${exp.icon} ${exp.color.split(' ')[1]} text-sm`}></i></div>
                  <span className={`text-xs font-semibold ${exp.color.split(' ')[1]}`}>{exp.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Facturas</p>
      </div>
    </div>
  );
}